import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useContext, useState } from "react";
import { MdOutlineMail, MdLockOutline, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { useNavigate, Link } from "react-router-dom";
import { Field } from "formik";
import { enqueueSnackbar } from "notistack";

import logoImg from "../../assets/logo.png";
import { AuthContext } from "../../contexts/AuthContext";
import { LoginSplashScreen } from "../../components/LoginSplashScreen";

interface SignIn {
  email: string;
  password: string;
}

const schema = Yup.object().shape({
  email: Yup.string()
    .email("E-mail inválido")
    .required("Campo email obrigatório"),
  password: Yup.string().required("Campo senha obrigatório"),
});

export function SignIn() {
  const { signIn } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string>("/tech-visit-reports");

  const navigate = useNavigate();

  async function handleSubmit({ email, password }: SignIn) {
    setIsLoading(true);
    try {
      // Converter email para minúsculas e remover espaços
      const sanitizedEmail = email.toLowerCase().trim();
      await signIn({ email: sanitizedEmail, password });

      const userData = localStorage.getItem("@LuviApp:user");
      if (userData) {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.isAdmin) {
          setRedirectPath("/home");
        } else {
          setRedirectPath("/tech-visit-reports");
        }
      }

      // Mostrar splash screen ao invés de navegar diretamente
      setShowSplash(true);
    } catch (error: any) {
      const message = error?.response?.data?.message || "E-mail ou senha incorretos";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  const handleSplashComplete = () => {
    navigate(redirectPath);
  };

  // Se está mostrando splash, renderizar apenas ele
  if (showSplash) {
    return <LoginSplashScreen onComplete={handleSplashComplete} duration={5000} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0085BD] via-[#006a96] to-[#004d6d]">
      {/* Background decorativo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#92C163]/10 rounded-full blur-3xl" />
      </div>

      {/* Card de Login */}
      <div className="relative w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-2xl p-4 shadow-lg">
              <img className="w-40 h-auto" src={logoImg} alt="Logo Luvilimp" />
            </div>
          </div>

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Bem-vindo de volta!
            </h1>
            <p className="text-white/70 text-sm">
              Entre com suas credenciais para continuar
            </p>
          </div>

          {/* Formulário */}
          <Formik
            initialValues={{
              email: "",
              password: "",
            }}
            validationSchema={schema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ errors, touched }) => (
              <Form className="space-y-5">
                {/* Campo Email */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    E-mail
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MdOutlineMail className="text-white/50" size={20} />
                    </div>
                    <Field
                      type="email"
                      name="email"
                      placeholder="seu@email.com"
                      className={`w-full h-14 pl-12 pr-4 bg-white/10 border ${
                        errors.email && touched.email
                          ? "border-red-400"
                          : "border-white/20 focus:border-white/50"
                      } rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all`}
                    />
                  </div>
                  {errors.email && touched.email && (
                    <p className="mt-1 text-red-300 text-xs">{errors.email}</p>
                  )}
                </div>

                {/* Campo Senha */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MdLockOutline className="text-white/50" size={20} />
                    </div>
                    <Field
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="••••••••"
                      className={`w-full h-14 pl-12 pr-12 bg-white/10 border ${
                        errors.password && touched.password
                          ? "border-red-400"
                          : "border-white/20 focus:border-white/50"
                      } rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/50 hover:text-white transition-colors"
                    >
                      {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <p className="mt-1 text-red-300 text-xs">{errors.password}</p>
                  )}
                </div>

                {/* Link Esqueci Senha */}
                <div className="flex justify-end">
                  <Link
                    className="text-sm text-white/70 hover:text-white transition-colors"
                    to="/forgot-password"
                  >
                    Esqueceu sua senha?
                  </Link>
                </div>

                {/* Botão Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-14 bg-[#92C163] hover:bg-[#7aab4e] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    "Entrar"
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>

        {/* Footer */}
        <p className="text-center text-white/50 text-sm mt-6">
          © {new Date().getFullYear()} Luvilimp. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
